import React, { useContext, useState, useEffect, useCallback, useMemo, useRef } from "react";
import { ModalWrapper } from "../../../../components/ui";
import { WorkerMultiSelect } from "../../../../components/ui";
import { Button } from "../../../../components/ui";
import { SearchableDropdown } from "../../../../components/ui";
import { TextMessagePreview } from "../../../../components/ui";
import SMSCostWarning from "./SMSCostWarning";
import { DispatchContext } from "../../../../context/domain/planning/DispatchContext";
import { sortAlphabetically } from "../../../../utils/collections/sort";
import { formatDate } from "../../../../utils/datetime";
import { X } from "../../../../components/icons/svg/outline";
import handleSendMessages from "../../../../utils/api/messaging";
import { updateDispatchStage } from "../../../../services/api/dispatch";
import { getSmsTemplates } from "../../../../services/api/core";
import { isDefaultLanguage, isSupportedLanguage } from "../../../../config";
import { personalizeSmsMessage } from "../../../../utils/business/worker";
import { useSmsTranslationPreviews } from "./useSmsTranslationPreview";
import ReviewResponseModal from "./ReviewResponseModal";

const SAMPLE_PREVIEW_WORKER = { first: "Alex" };
const TITLE_ID = "broadcast-sms-modal-title";
const TOOLBAR_BTN_CLASS =
  "rounded-full border border-gray-200 bg-white px-2.5 py-1 text-xs font-medium text-gray-600 transition hover:border-gray-300 hover:bg-gray-50 hover:text-gray-900 disabled:cursor-default disabled:opacity-40";
const SELECT_CLASS =
  "rounded-lg border border-gray-300 bg-white px-2.5 py-1.5 text-sm text-gray-800 outline-none focus:border-secondary-400 focus:ring-4 focus:ring-secondary-100";

const SELECT_BY_OPTIONS = {
  assigned: "All Assigned",
  offWork: "All Off Work",
  available: "All Available",
  byJob: "By Job",
};

/**
 * Broadcast modal for sending mass SMS to selected workers.
 * User selects recipients (all active users), enters a custom message, and sends.
 *
 * @param {Object} props
 * @param {Date} props.date - Selected day (for title)
 * @param {Object} [props.dayData] - Day-based worker sets for "Select by": assignedWorkerIds, offWorkWorkerIds, availableWorkerIds, jobsForDay
 * @param {boolean} props.isOpen
 * @param {function} props.onClose
 * @returns {JSX.Element|null}
 */
const BroadcastModal = ({ date, dayData, isOpen, onClose }) => {
  const { workers, reloadDays, runPlanningOperation } = useContext(DispatchContext);
  const [selectedWorkerIds, setSelectedWorkerIds] = useState([]);
  const [messageText, setMessageText] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [response, setResponse] = useState(null);
  const [isResponseOpen, setIsResponseOpen] = useState(false);
  const [selectByOption, setSelectByOption] = useState("");
  const [selectedJobId, setSelectedJobId] = useState("");
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const textareaRef = useRef(null);

  const data = dayData ?? {
    assignedWorkerIds: [],
    offWorkWorkerIds: [],
    availableWorkerIds: [],
    jobsForDay: [],
  };

  useEffect(() => {
    if (isOpen) {
      setSelectedWorkerIds([]);
      setMessageText("");
      setResponse(null);
      setIsResponseOpen(false);
      setSelectByOption("");
      setSelectedJobId("");
      setSelectedTemplateId(null);
    }
  }, [isOpen]);

  // Grow the composer to the message height so loaded templates are not clipped.
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) {
      return;
    }
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }, [messageText, isOpen]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }
    let cancelled = false;
    getSmsTemplates()
      .then((data) => {
        if (!cancelled) {
          setTemplates(Array.isArray(data) ? data : []);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setTemplates([]);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  /**
   * Dismiss the broadcast modal and clear send/response state.
   *
   * @param {void}
   * @returns {void}
   */
  const handleClose = useCallback(() => {
    onClose();
    setResponse(null);
    setIsResponseOpen(false);
    setMessageText("");
  }, [onClose]);

  const selectedWorkers = workers
    ? workers.filter((w) => selectedWorkerIds.includes(w.id))
    : [];
  const broadcastDateLabel = formatDate(date, "weekday MM/DD/YY");
  const messages = selectedWorkers.map((w) => ({
    worker: w,
    message: personalizeSmsMessage(messageText.trim(), w, {
      date: broadcastDateLabel,
    }),
  }));
  const templateOptions = useMemo(
    () =>
      sortAlphabetically(templates, "title").map((template) => ({
        value: template.id,
        label: template.title,
      })),
    [templates]
  );
  const previewWorker = selectedWorkers[0] || SAMPLE_PREVIEW_WORKER;
  const previewText = personalizeSmsMessage(messageText, previewWorker, {
    date: broadcastDateLabel,
  });
  const previewLanguages = [];
  selectedWorkers.forEach((worker) => {
    const code = String(worker?.preferred_language || "").trim().toLowerCase();
    if (
      code &&
      !isDefaultLanguage(code) &&
      isSupportedLanguage(code) &&
      !previewLanguages.includes(code)
    ) {
      previewLanguages.push(code);
    }
  });
  const translatedPreviews = useSmsTranslationPreviews(previewText, {
    enabled: isOpen && previewLanguages.length > 0,
    targetLanguages: previewLanguages,
    debounceMs: 400,
  });
  const canSend =
    selectedWorkerIds.length > 0 &&
    messageText.trim().length > 0 &&
    !isSending;

  /**
   * Send the typed message to the current recipient set, record a Broadcast
   * history row, and open the result modal.
   *
   * @param {void}
   * @returns {Promise<void>}
   */
  const handleSendClick = async () => {
    if (!canSend) return;
    setIsSending(true);
    try {
      const res = await handleSendMessages(messages);
      setResponse(res);
      setIsResponseOpen(true);
    } catch (error) {
      setResponse({
        error:
          error.response?.data?.error || "Some SMS messages failed to send",
        errors: error.response?.data?.errors || [],
        success: error.response?.data?.success || [],
      });
      setIsResponseOpen(true);
    }
    try {
      await runPlanningOperation({
        module: "dispatch",
        operation: "markDayBroadcast",
        resources: ["dispatch.dispatch_stage"],
        apiCall: () =>
          updateDispatchStage("Broadcast", date, null, {
            message: messageText.trim(),
            userIds: selectedWorkerIds,
          }),
      });
      await reloadDays();
    } catch (error) {
      console.error(error);
    } finally {
      setIsSending(false);
    }
  };

  /**
   * Select every worker in the Dispatch catalog.
   *
   * @param {void}
   * @returns {void}
   */
  const handleSelectAll = () => {
    if (workers) setSelectedWorkerIds(workers.map((w) => w.id));
  };
  /**
   * Clear the current recipient selection.
   *
   * @param {void}
   * @returns {void}
   */
  const handleClearRecipients = () => setSelectedWorkerIds([]);

  /**
   * Load a template body into the composer. Clearing the picker leaves the text.
   *
   * @param {string|number|null} templateId Selected template id, or null when cleared.
   * @returns {void}
   */
  const handleTemplateChange = (templateId) => {
    setSelectedTemplateId(templateId);
    if (templateId == null || templateId === "") {
      return;
    }
    const template = templates.find((row) => String(row.id) === String(templateId));
    if (template) {
      setMessageText(template.body || "");
    }
  };

  /**
   * Insert `{{token}}` at the textarea caret, or append if there is no selection.
   *
   * @param {string} token Token name without braces (`name`, `date`).
   * @returns {void}
   */
  const handleInsertToken = (token) => {
    const el = textareaRef.current;
    const start = el?.selectionStart ?? messageText.length;
    const end = el?.selectionEnd ?? start;
    const wrapped = `{{${token}}}`;
    setMessageText(`${messageText.slice(0, start)}${wrapped}${messageText.slice(end)}`);
    const caret = start + wrapped.length;
    requestAnimationFrame(() => {
      if (!el) {
        return;
      }
      el.focus();
      el.setSelectionRange(caret, caret);
    });
  };

  /**
   * Replace selected worker ids from a day-based category or one job's crew.
   *
   * @param {string} option Select-by key (`assigned`, `offWork`, `available`, `byJob`).
   * @param {string} jobId Job id when `option` is `byJob`.
   * @returns {void}
   */
  const applySelectBy = useCallback(
    (option, jobId) => {
      if (!option) {
        setSelectedWorkerIds([]);
      } else if (option === "assigned") {
        setSelectedWorkerIds(data.assignedWorkerIds ?? []);
      } else if (option === "offWork") {
        setSelectedWorkerIds(data.offWorkWorkerIds ?? []);
      } else if (option === "available") {
        setSelectedWorkerIds(data.availableWorkerIds ?? []);
      } else if (option === "byJob" && jobId) {
        const jobEntry = data.jobsForDay?.find(
          (e) => String(e.job?.id) === String(jobId)
        );
        setSelectedWorkerIds(jobEntry?.workerIds ?? []);
      } else if (option === "byJob") {
        setSelectedWorkerIds([]);
      }
    },
    [data.assignedWorkerIds, data.offWorkWorkerIds, data.availableWorkerIds, data.jobsForDay]
  );

  if (!isOpen) return null;

  return (
    <>
      <ModalWrapper
        isOpen={isOpen}
        onClose={handleClose}
        closeButton={false}
        maxWidth={800}
        className="relative w-full max-h-[90vh] overflow-y-auto rounded-xl bg-white p-0 shadow-xl"
        outsideClickIgnoreSelector="[data-searchable-dropdown-listbox]"
      >
        <div role="dialog" aria-modal="true" aria-labelledby={TITLE_ID}>
          <div className="flex items-center justify-between gap-3 bg-primary px-5 py-3.5">
            <div className="flex items-center gap-2.5">
              <h2
                id={TITLE_ID}
                className="text-lg font-semibold leading-none tracking-tight text-white"
              >
                Broadcast SMS
              </h2>
              <p className="text-sm leading-none text-white/80">
                {formatDate(date, "weekday MM/DD/YY")}
              </p>
            </div>
            <button
              type="button"
              onClick={handleClose}
              className="inline-flex h-7 w-7 flex-shrink-0 items-center justify-center rounded text-primary-100 transition hover:bg-white/10 hover:text-white focus:outline-none focus:ring-2 focus:ring-white/30"
              aria-label="Close"
            >
              <X className="h-4 w-4" aria-hidden />
            </button>
          </div>

          <div className="space-y-2 px-5 py-4 pt-2">
            <section>
              {workers && workers.length > 0 ? (
                <div className="mb-2 flex flex-wrap items-center gap-2">
                  <label
                    className="text-xs font-medium text-gray-500"
                    htmlFor="broadcast-select-by"
                  >
                    Select by
                  </label>
                  <select
                    id="broadcast-select-by"
                    value={selectByOption}
                    onChange={(e) => {
                      const value = e.target.value;
                      setSelectByOption(value);
                      applySelectBy(value, value === "byJob" ? selectedJobId : "");
                    }}
                    className={SELECT_CLASS}
                    aria-label="Select by category"
                  >
                    <option value="">None</option>
                    <option value="byJob" className="font-bold" style={{ fontWeight: 700 }}>
                      {SELECT_BY_OPTIONS.byJob}
                    </option>
                    <option value="assigned">{SELECT_BY_OPTIONS.assigned}</option>
                    <option value="offWork">{SELECT_BY_OPTIONS.offWork}</option>
                    <option value="available">{SELECT_BY_OPTIONS.available}</option>
                  </select>
                  {selectByOption === "byJob" ? (
                    <select
                      value={selectedJobId}
                      onChange={(e) => {
                        const value = e.target.value;
                        setSelectedJobId(value);
                        applySelectBy("byJob", value);
                      }}
                      className={SELECT_CLASS}
                      aria-label="Select job"
                    >
                      <option value="">Select job...</option>
                      {data.jobsForDay?.map((entry) => (
                        <option key={entry.job?.id} value={entry.job?.id}>
                          {entry.job?.number} - {entry.job?.description}
                        </option>
                      ))}
                    </select>
                  ) : null}
                </div>
              ) : null}
              <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <h3 className="text-sm font-semibold text-gray-800">
                    Recipients
                  </h3>
                  <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
                    {selectedWorkerIds.length} selected
                  </span>
                </div>
                {workers && workers.length > 0 ? (
                  <div className="flex items-center gap-1.5">
                    <button
                      type="button"
                      onClick={handleSelectAll}
                      className={TOOLBAR_BTN_CLASS}
                    >
                      Select all
                    </button>
                    <button
                      type="button"
                      onClick={handleClearRecipients}
                      className={TOOLBAR_BTN_CLASS}
                    >
                      Clear
                    </button>
                  </div>
                ) : null}
              </div>
              <div className="rounded-lg border border-gray-200 bg-gray-50/80 px-3 py-2">
                {workers ? (
                  <WorkerMultiSelect
                    workers={workers}
                    selectedWorkerIds={selectedWorkerIds}
                    onChange={setSelectedWorkerIds}
                    disabledWorkerIds={[]}
                    byCategory={false}
                    showRoles={false}
                    multiCol={true}
                    includeLanguage
                  />
                ) : (
                  <p className="py-6 text-center text-sm text-gray-500">
                    Loading workers...
                  </p>
                )}
              </div>
            </section>

            <section>
              <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <h3 className="text-sm font-semibold text-gray-800">Message</h3>
                  <p className="text-xs text-gray-500">(Editable)</p>
                </div>
                <div className="flex min-w-0 flex-wrap items-center justify-end gap-1.5">
                  <button
                    type="button"
                    onClick={() => handleInsertToken("name")}
                    className={TOOLBAR_BTN_CLASS}
                  >
                    Insert name
                  </button>
                  <button
                    type="button"
                    onClick={() => handleInsertToken("date")}
                    className={TOOLBAR_BTN_CLASS}
                  >
                    Insert date
                  </button>
                  <SearchableDropdown
                    options={templateOptions}
                    value={selectedTemplateId}
                    onChange={handleTemplateChange}
                    placeholder="Load template..."
                    ariaLabel="Load SMS template"
                    searchWhenOverflow
                    className="w-44"
                    inputClassName="!py-1"
                    listboxClassName="z-[10060]"
                  />
                </div>
              </div>
              <textarea
                ref={textareaRef}
                className="min-h-[96px] w-full resize-none overflow-hidden rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-900 outline-none transition placeholder:text-gray-400 focus:border-secondary-400 focus:ring-4 focus:ring-secondary-100"
                placeholder="Write your message..."
                value={messageText}
                onChange={(e) => setMessageText(e.target.value)}
                rows={4}
              />
              {messageText.trim() ? (
                <div className="mt-3 rounded-lg bg-gray-50 px-3 py-3">
                  <TextMessagePreview
                    text={previewText}
                    extraTexts={translatedPreviews}
                    label={
                      translatedPreviews.length
                        ? "Sample Message Preview (Includes translation)"
                        : "Sample Message Preview"
                    }
                  />
                </div>
              ) : null}
            </section>

            <SMSCostWarning messageCount={selectedWorkerIds.length} />
            <div
              title={
                !messageText.trim().length
                  ? "Please type a message before sending"
                  : undefined
              }
            >
              <Button
                variant={canSend ? "secondary" : "disabled"}
                text={isSending ? "Sending..." : "Send Messages"}
                onClick={handleSendClick}
                disabled={!canSend}
                className="w-full rounded-lg"
              />
            </div>
          </div>
        </div>
      </ModalWrapper>
      {response && (
        <ReviewResponseModal
          response={response}
          isOpen={isResponseOpen}
          setIsOpen={setIsResponseOpen}
          onClose={handleClose}
        />
      )}
    </>
  );
};

export default BroadcastModal;
